(ns ycweb.app.admin.tech.system.featureflag.overview
  (:require
   [clojure.string :as str]
   [rum.core :as rum]
   [ycweb.app.dashboard.common :as dcommon]
   [ycweb.app.dashboard.presence :as presence]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.datatables.datatables-v2 :as datatables]
   #?@(:cljs [[ycweb.components.datatables.wrapper.ajax :as ajax]])
   [ycweb.components.feedback :as feedback]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;; ============================================================================
;; State keys
;; ============================================================================

(def reports-datatable-key :admin.feature-flag/reports)

(def changelog-datatable-key :admin.feature-flag/change-log)

(def overview-keys
  [:ff-overview :ff-groups :ff-available-groups :ff-staff-teams :ff-mrp-associate-count
   :ff-expanded-groups :ff-user-search :ff-metadata-pairs])

(declare parse-metadata-pairs)

;; ============================================================================
;; Data Receive Handlers
;; ============================================================================

(defmethod comms/receive :admin.feature-flag/overview [app-atom response]
  (let [process-atom (cmn/process-cursor app-atom)
        {:keys [feature-flag groups available-groups staff-teams mrp-associate-count]} response]
    (toaster/close-overlay app-atom :admin.feature-flag/overview)
    (cond
      (not (:success response))
      (swap! process-atom assoc :ff-overview nil)

      :else
      (do
        (swap! process-atom assoc
          :ff-overview feature-flag
          :ff-groups (vec groups)
          :ff-available-groups (vec available-groups)
          :ff-staff-teams (vec staff-teams)
          :ff-mrp-associate-count mrp-associate-count
          :ff-metadata-pairs (or (parse-metadata-pairs (:feature-flag/metadata feature-flag)) []))
        (grid/dispatch-action app-atom :admin.feature-flag/decisions-list {})
        (grid/dispatch-action app-atom :admin.feature-flag/change-log-list {})))))

(defn- refresh-overview [app-atom]
  (let [screen-params (cmn/screen-params app-atom)
        flag-uuid (:uuid screen-params)]
    (toaster/open-overlay app-atom :admin.feature-flag/overview)
    (comms/send :admin.feature-flag/overview {:uuid flag-uuid})))

(defmethod comms/receive :admin.feature-flag/update-description
  [app-atom {:keys [success description]}]
  (toaster/close-overlay app-atom :admin.feature-flag/update-description)
  (when success
    (grid/close-modal app-atom)
    (let [process-atom (cmn/process-cursor app-atom)]
      (swap! process-atom assoc-in [:ff-overview :feature-flag/description] description))))

(defmethod comms/receive :admin.feature-flag/add-group [app-atom {:keys [success error]}]
  (toaster/close-overlay app-atom :admin.feature-flag/group-action)
  (cond
    success
    (do
      (grid/close-modal app-atom)
      (refresh-overview app-atom))

    :else
    (grid/message-modal app-atom
      {:title "Couldn't add group"
       :text (or error "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/create-and-link-group
  [app-atom {:keys [success error]}]
  (toaster/close-overlay app-atom :admin.feature-flag/group-action)
  (cond
    success
    (do
      (grid/close-modal app-atom)
      (refresh-overview app-atom))

    :else
    (grid/message-modal app-atom
      {:title "Couldn't create group"
       :text (or error "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/remove-group [app-atom {:keys [success msg]}]
  (toaster/close-overlay app-atom :admin.feature-flag/group-action)
  (cond
    success
    (refresh-overview app-atom)

    :else
    (grid/message-modal app-atom
      {:title "Couldn't unlink group"
       :text (or msg "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/add-user-to-group
  [app-atom {:keys [success group-id user msg]}]
  (toaster/close-overlay app-atom :admin.feature-flag/user-action)
  (cond
    success
    (let [process-atom (cmn/process-cursor app-atom)]
      (swap! process-atom update :ff-groups
        (fn [groups]
          (mapv
            (fn [g]
              (cond
                (= (:group_id g) group-id)
                (-> g
                  (update :users (fn [users] (vec (conj (or users []) user))))
                  (update :user_count (fnil inc 0)))

                :else g))
            groups)))
      ;; Keep the modal open for multiple adds; drop the freshly-added user from visible results.
      (swap! process-atom update :ff-user-search
        (fn [users] (vec (remove #(= (:id %) (:user_id user)) users)))))

    :else
    (grid/message-modal app-atom
      {:title "Couldn't add user"
       :text (or msg "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/remove-user-from-group
  [app-atom {:keys [success grouplink-id group-id]}]
  (toaster/close-overlay app-atom :admin.feature-flag/user-action)
  (when success
    (let [process-atom (cmn/process-cursor app-atom)]
      (swap! process-atom update :ff-groups
        (fn [groups]
          (mapv
            (fn [g]
              (cond
                (= (:group_id g) group-id)
                (-> g
                  (update :users
                    (fn [users] (vec (remove #(= (:grouplink_id %) grouplink-id) users))))
                  (update :user_count (fnil dec 0)))

                :else g))
            groups))))))

(defmethod comms/receive :admin.feature-flag/add-staff-to-group
  [app-atom {:keys [success added skipped msg]}]
  (toaster/close-overlay app-atom :admin.feature-flag/user-action)
  (cond
    success
    (do
      (grid/close-modal app-atom)
      (toaster/open-toast app-atom :success
        (str added " staff added, " skipped " already in the group")
        {:closetime 5000})
      (refresh-overview app-atom))

    :else
    (grid/message-modal app-atom
      {:title "Couldn't add staff"
       :text (or msg "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/remove-all-users-from-group
  [app-atom {:keys [success removed msg]}]
  (toaster/close-overlay app-atom :admin.feature-flag/user-action)
  (cond
    success
    (do
      (toaster/open-toast app-atom :success
        (str removed " users removed from the group")
        {:closetime 5000})
      (refresh-overview app-atom))

    :else
    (grid/message-modal app-atom
      {:title "Couldn't remove users"
       :text (or msg "Something went wrong.")
       :confirm-text "OK"})))

(defmethod comms/receive :admin.feature-flag/user-search [app-atom {:keys [users]}]
  (toaster/close-overlay app-atom :admin.feature-flag/user-search)
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom assoc :ff-user-search (vec users))))

;; ============================================================================
;; Header
;; ============================================================================

(rum/defc overview-header < rum/reactive cmn/cursors [_ flag]
  [:div.u-margin-bottom-30
   {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush-force])}
   [:div
    {:class (u/bem :flex-grid-block [:fluid])}
    [:div.flex-grid-content
     (headings/heading (or (:feature-flag/feature-key flag) "Feature Flag")
       {:type :h1 :size :large :flush true})]]
   [:div
    {:class (u/bem :flex-grid-block [:align-right :fluid])}
    [:div.flex-grid-content
     [:span
      {:style {:background-color (if (:feature-flag/active flag) "#90EE90" "#ccc")
               :padding "4px 12px"
               :border-radius "4px"
               :font-size "14px"
               :font-weight "bold"
               :margin-right "12px"}}
      (if (:feature-flag/active flag) "Active" "Inactive")]
     (buttons/button-web "Back"
       {:style :secondary
        :icon :arrow-left
        :on-click #(urlstate/back)})]]])

;; ============================================================================
;; Stats Summary Card
;; ============================================================================

(rum/defc stats-content [flag]
  [:div.u-margin-bottom-20
   (headings/heading "Summary" {:type :h4 :size :small :flush true})
   [:div
    {:class (u/bem :flex-grid-group [:align-left :valign-middle :flush])
     :style {:padding "6px 0px"}}
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Threshold %: "] (:feature-flag/value flag)]]
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Total Decisions: "] (or (:feature-flag/total-decisions flag) 0)]]
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Approved: "] (or (:feature-flag/total-decision-approved flag) 0)]]
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Current Capacity %: "] (or (:feature-flag/total-decision-threshold flag) 0)]]
    [:div
     {:class (u/bem :flex-grid-block [:ml-shrink-0 :ml-fluid])}
     [:div.flex-grid-content
      [:b "Reporting Mode: "] (or (:feature-flag/reporting-mode flag) "on")]]]])

;; ============================================================================
;; Description Section
;; ============================================================================

(defmethod grid/action :admin.feature-flag/save-description [app-atom {:keys [feature-flag-id]} _]
  (let [modal-atom (grid/modal-cursor app-atom)
        description (cmn/curact modal-atom [:config :description])]
    (toaster/open-overlay app-atom :admin.feature-flag/update-description)
    (comms/send :admin.feature-flag/update-description
      {:feature-flag-id feature-flag-id
       :description (or description "")})))

(rum/defc description-edit-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [feature-key (cmn/curact modal-atom :feature-key)
        description-atom (rum/cursor modal-atom :description)]
    [:div
     (headings/heading "Edit Description"
       {:type :h4 :size :medium :subtext feature-key})
     [:div {:style {:margin-top "16px"}}
      (headings/heading "Description" {:type :h5 :size :tiny})
      (forms/textarea-raw description-atom
        {:placeholder "Describe what this flag controls and any context developers should know..."
         :rows 5})]
     (toaster/loading-overlay app-atom :admin.feature-flag/update-description)]))

(rum/defc description-edit-modal-footer [app-atom modal-atom _]
  (let [feature-flag-id (cmn/curact modal-atom :feature-flag-id)]
    (grid/button-row
      {:left-text "Cancel"
       :left-no-icon true
       :left-fn #(grid/close-modal app-atom)
       :right-text "Save"
       :right-no-icon true
       :right-fn
       #(grid/dispatch-modal-action app-atom :admin.feature-flag/save-description
          {:feature-flag-id feature-flag-id} {:prevent-closing true})})))

(defmethod grid/modal-config :admin.feature-flag/edit-description [_ _ _]
  {:body description-edit-modal-body
   :footer description-edit-modal-footer
   :cancel-key :admin.feature-flag/close-edit-description
   :layout :modal
   :modal-type :details
   :max-width 600})

(defmethod grid/action :admin.feature-flag/open-edit-description
  [app-atom {:keys [feature-flag-id feature-key description]} _]
  (grid/open-modal app-atom :admin.feature-flag/edit-description
    {:feature-flag-id feature-flag-id
     :feature-key feature-key
     :description (or description "")}))

(rum/defc description-content < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        description (cmn/curact process-atom [:ff-overview :feature-flag/description])
        feature-flag-id (cmn/curact process-atom [:ff-overview :feature-flag/id])
        feature-key (cmn/curact process-atom [:ff-overview :feature-flag/feature-key])
        blank? (str/blank? description)]
    [:div.u-margin-bottom-20
     [:div
      {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (headings/heading "Description" {:type :h4 :size :small :flush true})]]
      [:div
       {:class (u/bem :flex-grid-block [:align-right :fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Edit"
          {:style :secondary
           :icon :pencil
           :size :small
           :on-click
           #(grid/dispatch-action app-atom :admin.feature-flag/open-edit-description
              {:feature-flag-id feature-flag-id
               :feature-key feature-key
               :description description})})]]]
     [:div {:style {:padding "8px 0"
                    :color (if blank? "#999" "inherit")}}
      (if blank?
        "No description. Click Edit to add one."
        description)]]))

;; ============================================================================
;; Add Group Modal
;; ============================================================================

(defmethod grid/action :admin.feature-flag/submit-add-group [app-atom _ _]
  (let [modal-atom (grid/modal-cursor app-atom)
        feature-flag-id (cmn/curact modal-atom [:config :feature-flag-id])
        group-id (cmn/curact modal-atom [:config :selected-group-id])]
    (cond
      (nil? group-id)
      (grid/message-modal app-atom
        {:title "Pick a group"
         :text "Use the search field to pick a group first."
         :confirm-text "OK"})

      :else
      (do
        (toaster/open-overlay app-atom :admin.feature-flag/group-action)
        (comms/send :admin.feature-flag/add-group
          {:feature-flag-id feature-flag-id
           :group-id group-id})))))

(rum/defc add-group-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [unlinked-groups (cmn/curact modal-atom :unlinked-groups)
        selected-name (cmn/curact modal-atom :selected-group-name)]
    [:div
     (headings/heading "Add Group" {:type :h4 :size :medium})
     [:p.u-margin-top-8 {:style {:color "#666"}}
      "Users in the selected group will have the flag enabled."]
     (cond
       (seq unlinked-groups)
       [:div.u-margin-top-12
        (forms/inline-autocomplete-select
          [:span.u-block.faux-input-combo__placeholder
           (or selected-name "Search for a group...")]
          unlinked-groups
          {:select-style? true
           :suggestion-hint "Type to search groups"
           :empty-list-hint "No groups found"
           :item-render-fn :fullname
           :filter-fn
           (fn [item search-term]
             (str/includes?
               (str/lower-case (:fullname item))
               (str/lower-case search-term)))
           :select-fn
           (fn [group]
             (swap! modal-atom assoc-in [:config :selected-group-id] (:id group))
             (swap! modal-atom assoc-in [:config :selected-group-name] (:fullname group)))})]

       :else
       [:p.u-margin-top-12 {:style {:color "#666"}}
        "All available groups are already linked to this flag."])
     (toaster/loading-overlay app-atom :admin.feature-flag/group-action)]))

(rum/defc add-group-modal-footer [app-atom _ _]
  (grid/button-row
    {:left-text "Cancel"
     :left-no-icon true
     :left-fn #(grid/close-modal app-atom)
     :right-text "Add"
     :right-no-icon true
     :right-fn
     #(grid/dispatch-modal-action app-atom :admin.feature-flag/submit-add-group {}
        {:prevent-closing true})}))

(defmethod grid/modal-config :admin.feature-flag/add-group-modal [_ _ _]
  {:body add-group-modal-body
   :footer add-group-modal-footer
   :cancel-key :admin.feature-flag/close-add-group-modal
   :layout :modal
   :modal-type :details
   :max-width 600})

(defmethod grid/action :admin.feature-flag/open-add-group-modal
  [app-atom {:keys [feature-flag-id unlinked-groups]} _]
  (grid/open-modal app-atom :admin.feature-flag/add-group-modal
    {:feature-flag-id feature-flag-id
     :unlinked-groups (vec unlinked-groups)
     :selected-group-id nil
     :selected-group-name nil}))

;; ============================================================================
;; Create Group Modal
;; ============================================================================

(defn- feature-key->group-suffix
  "Converts a feature key to a PascalCase group name suffix.
   e.g. \"web/product-page-new\" -> \"WebProductPageNew\""
  [feature-key]
  (->>
    (str/split (or feature-key "") #"[/\-]")
    (map str/capitalize)
    (str/join)))

(defmethod grid/action :admin.feature-flag/submit-create-group [app-atom _ _]
  (let [modal-atom (grid/modal-cursor app-atom)
        feature-flag-id (cmn/curact modal-atom [:config :feature-flag-id])
        group-name (str/trim (or (cmn/curact modal-atom [:config :group-name]) ""))]
    (cond
      (str/blank? group-name)
      (grid/message-modal app-atom
        {:title "Name required"
         :text "Enter a group name."
         :confirm-text "OK"})

      :else
      (do
        (toaster/open-overlay app-atom :admin.feature-flag/group-action)
        (comms/send :admin.feature-flag/create-and-link-group
          {:feature-flag-id feature-flag-id
           :group-name group-name})))))

(rum/defc create-group-modal-body < rum/reactive cmn/cursors [_ modal-atom _]
  (let [group-name-atom (rum/cursor modal-atom :group-name)]
    [:div
     (headings/heading "Create Group" {:type :h4 :size :medium})
     [:p.u-margin-top-8 {:style {:color "#666"}}
      "Create a new permission group and link it to this flag. Users added to this group will have the flag enabled."]
     [:div.u-margin-top-12
      [:div {:style {:display "flex" :align-items "center"}}
       [:span {:style {:font-weight "bold" :white-space "nowrap" :margin-right "4px"}}
        "SiteAdmin:FeatureFlag:"]
       (forms/input-raw "Group name" group-name-atom
         {:placeholder "e.g. WebProductPageNew"})]]]))

(rum/defc create-group-modal-footer [app-atom _ _]
  (grid/button-row
    {:left-text "Cancel"
     :left-no-icon true
     :left-fn #(grid/close-modal app-atom)
     :right-text "Create"
     :right-no-icon true
     :right-fn
     #(grid/dispatch-modal-action app-atom :admin.feature-flag/submit-create-group {}
        {:prevent-closing true})}))

(defmethod grid/modal-config :admin.feature-flag/create-group-modal [_ _ _]
  {:body create-group-modal-body
   :footer create-group-modal-footer
   :cancel-key :admin.feature-flag/close-create-group-modal
   :layout :modal
   :modal-type :details
   :max-width 600})

(defmethod grid/action :admin.feature-flag/open-create-group-modal
  [app-atom {:keys [feature-flag-id feature-key]} _]
  (grid/open-modal app-atom :admin.feature-flag/create-group-modal
    {:feature-flag-id feature-flag-id
     :group-name (feature-key->group-suffix feature-key)}))

;; ============================================================================
;; Add User Modal
;; ============================================================================

(rum/defc add-user-result-row < rum/reactive cmn/cursors
  [app-atom feature-flag-id group-id user]
  [:tr
   [:td {:style {:padding "6px" :border "1px solid #ddd"}}
    (str (:firstname user) " " (:lastname user))]
   [:td {:style {:padding "6px" :border "1px solid #ddd"}}
    (:email user)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    (buttons/button "Add"
      (fn []
        (toaster/open-overlay app-atom :admin.feature-flag/user-action)
        (comms/send :admin.feature-flag/add-user-to-group
          {:feature-flag-id feature-flag-id
           :group-id group-id
           :user-id (:id user)}))
      {:small true})]])

(rum/defcs add-user-modal-body
  < rum/reactive cmn/cursors
  {:will-mount
   (fn [state]
     (let [[app-atom modal-atom _] (:rum/args state)
           process-atom (cmn/process-cursor app-atom)
           query-atom (rum/cursor modal-atom :query)]
       ;; Debounce so we only hit the backend once the user stops typing for 400ms.
       (cmn/debounce-atom query-atom 400
         (fn [q]
           (let [q (str/trim (or q ""))]
             (cond
               (< (count q) 2)
               (swap! process-atom assoc :ff-user-search [])

               :else
               (do
                 (toaster/open-overlay app-atom :admin.feature-flag/user-search)
                 (comms/send :admin.feature-flag/user-search {:query q}))))))
       state))}
  [_ app-atom modal-atom _]
  (let [process-atom (cmn/process-cursor app-atom)
        feature-flag-id (cmn/curact modal-atom :feature-flag-id)
        group-id (cmn/curact modal-atom :group-id)
        group-name (cmn/curact modal-atom :group-name)
        existing-user-ids (or (cmn/curact modal-atom :existing-user-ids) #{})
        query-atom (rum/cursor modal-atom :query)
        results (or (cmn/curact process-atom :ff-user-search) [])
        filtered (remove #(contains? existing-user-ids (:id %)) results)]
    [:div
     (headings/heading "Add User to Group"
       {:type :h4 :size :medium :subtext group-name})
     [:p.u-margin-top-8 {:style {:color "#666" :font-size "12px"}}
      "Type at least 2 characters to search by name or email."]
     [:div.u-margin-top-8
      (forms/input-raw "Search users..." query-atom
        {:placeholder "Search users by name or email..."})]
     [:div.u-relative.u-margin-top-12
      (cond
        (seq filtered)
        [:table {:class (u/bem :oTable [:has-shadow]) :style {:font-size "13px"}}
         [:thead
          [:tr
           [:th.u-text-left "Name"]
           [:th.u-text-left "Email"]
           [:th.u-text-center {:style {:width "100px"}} "Action"]]]
         (into [:tbody]
           (map #(rum/with-key
                   (add-user-result-row app-atom feature-flag-id group-id %)
                   (:id %))
             filtered))]

        (and (>= (count (or @query-atom "")) 2) (empty? filtered))
        [:p {:style {:color "#666"}} "No matching users found."]

        :else
        [:p {:style {:color "#999"}} "Results will appear here."])
      (toaster/loading-overlay app-atom :admin.feature-flag/user-search)]
     (toaster/loading-overlay app-atom :admin.feature-flag/user-action)]))

(rum/defc add-user-modal-footer [app-atom _ _]
  (grid/button-row
    {:left-text "Close"
     :left-no-icon true
     :left-fn #(grid/close-modal app-atom)
     :right-text "Done"
     :right-no-icon true
     :right-fn #(grid/close-modal app-atom)}))

(defmethod grid/modal-config :admin.feature-flag/add-user-modal [_ _ _]
  {:body add-user-modal-body
   :footer add-user-modal-footer
   :cancel-key :admin.feature-flag/close-add-user-modal
   :layout :modal
   :modal-type :details
   :max-width 700})

(defmethod grid/action :admin.feature-flag/open-add-user-modal
  [app-atom {:keys [feature-flag-id group-id group-name existing-user-ids]} _]
  (let [process-atom (cmn/process-cursor app-atom)]
    (swap! process-atom assoc :ff-user-search [])
    (grid/open-modal app-atom :admin.feature-flag/add-user-modal
      {:feature-flag-id feature-flag-id
       :group-id group-id
       :group-name group-name
       :existing-user-ids (set existing-user-ids)
       :query ""})))

;; ============================================================================
;; Add Staff Modal
;; ============================================================================

(defmethod grid/action :admin.feature-flag/submit-add-staff [app-atom _ _]
  (let [modal-atom (grid/modal-cursor app-atom)
        feature-flag-id (cmn/curact modal-atom [:config :feature-flag-id])
        group-id (cmn/curact modal-atom [:config :group-id])
        team (or (cmn/curact modal-atom [:config :team]) "")]
    (toaster/open-overlay app-atom :admin.feature-flag/user-action)
    (comms/send :admin.feature-flag/add-staff-to-group
      {:feature-flag-id feature-flag-id
       :group-id group-id
       :discount-level "Yuppiechef"
       :team team})))

(rum/defc add-staff-modal-body < rum/reactive cmn/cursors [app-atom modal-atom _]
  (let [process-atom (cmn/process-cursor app-atom)
        teams (or (cmn/curact process-atom :ff-staff-teams) [])
        total (reduce + 0 (map :staff_count teams))
        group-name (cmn/curact modal-atom :group-name)]
    [:div
     (headings/heading "Add Staff to Group"
       {:type :h4 :size :medium :subtext group-name})
     [:p.u-margin-top-8 {:style {:color "#666"}}
      "Adds every active Yuppiechef staff member, or one team, to this group. Mr Price Associates are not included. Staff already in the group are left as they are."]
     [:div.u-margin-top-12
      (forms/select "Staff to add"
        (rum/cursor modal-atom :team)
        (into [{:value "" :text (str "All active Yuppiechef staff (" total ")")}]
          (map
            (fn [{:keys [team staff_count]}]
              {:value team :text (str team " (" staff_count ")")})
            (remove #(str/blank? (:team %)) teams)))
        {:default ""})]
     (toaster/loading-overlay app-atom :admin.feature-flag/user-action)]))

(rum/defc add-staff-modal-footer [app-atom _ _]
  (grid/button-row
    {:left-text "Cancel"
     :left-no-icon true
     :left-fn #(grid/close-modal app-atom)
     :right-text "Add"
     :right-no-icon true
     :right-fn
     #(grid/dispatch-modal-action app-atom :admin.feature-flag/submit-add-staff {}
        {:prevent-closing true})}))

(defmethod grid/modal-config :admin.feature-flag/add-staff-modal [_ _ _]
  {:body add-staff-modal-body
   :footer add-staff-modal-footer
   :cancel-key :admin.feature-flag/close-add-staff-modal
   :layout :modal
   :modal-type :details
   :max-width 600})

(defmethod grid/action :admin.feature-flag/open-add-staff-modal
  [app-atom {:keys [feature-flag-id group-id group-name]} _]
  (grid/open-modal app-atom :admin.feature-flag/add-staff-modal
    {:feature-flag-id feature-flag-id
     :group-id group-id
     :group-name group-name
     :team ""}))

;; ============================================================================
;; Add Mr Price Associates
;; ============================================================================

(defmethod grid/action :admin.feature-flag/confirm-add-mrp-associates
  [app-atom {:keys [feature-flag-id group-id group-name]} _]
  (let [associate-count (:ff-mrp-associate-count @(cmn/process-cursor app-atom))]
    (grid/confirm-modal app-atom
      {:title "Add Mr Price Associates"
       :text (str "Add all " associate-count " active Mr Price Associates to " group-name "? Associates already in the group are left as they are.")
       :confirm-key :admin.feature-flag/add-mrp-associates-to-group
       :confirm-text "Add"
       :cancel-text "Cancel"
       :feature-flag-id feature-flag-id
       :group-id group-id})))

(defmethod grid/action :admin.feature-flag/add-mrp-associates-to-group
  [app-atom {:keys [feature-flag-id group-id]} _]
  (toaster/open-overlay app-atom :admin.feature-flag/user-action)
  (comms/send :admin.feature-flag/add-staff-to-group
    {:feature-flag-id feature-flag-id
     :group-id group-id
     :discount-level "Mr Price Associate"
     :team ""}))

;; ============================================================================
;; Remove All Users
;; ============================================================================

(defmethod grid/action :admin.feature-flag/confirm-remove-all-users
  [app-atom {:keys [feature-flag-id group-id group-name user-count]} _]
  (grid/confirm-modal app-atom
    {:title "Remove all users"
     :text (str "Remove all " user-count " users from " group-name "? The flag switches off for them on their next login.")
     :confirm-key :admin.feature-flag/remove-all-users-from-group
     :confirm-text "Yes, remove all"
     :cancel-text "Cancel"
     :negative true
     :feature-flag-id feature-flag-id
     :group-id group-id}))

(defmethod grid/action :admin.feature-flag/remove-all-users-from-group
  [app-atom {:keys [feature-flag-id group-id]} _]
  (toaster/open-overlay app-atom :admin.feature-flag/user-action)
  (comms/send :admin.feature-flag/remove-all-users-from-group
    {:feature-flag-id feature-flag-id
     :group-id group-id}))

;; ============================================================================
;; Groups Section (with inline user membership)
;; ============================================================================

(defn- toggle-expanded [process-atom group-id]
  (swap! process-atom update :ff-expanded-groups
    (fn [expanded]
      (let [s (set (or expanded #{}))]
        (cond
          (contains? s group-id) (disj s group-id)
          :else (conj s group-id))))))

(rum/defc user-row < rum/reactive cmn/cursors [app-atom feature-flag-id user]
  [:tr
   [:td {:style {:padding "6px" :border "1px solid #ddd"}}
    (str (:firstname user) " " (:lastname user))]
   [:td {:style {:padding "6px" :border "1px solid #ddd"}}
    (:email user)]
   [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
    [:a.u-cursor-pointer
     {:on-click
      (fn [_]
        (toaster/open-overlay app-atom :admin.feature-flag/user-action)
        (comms/send :admin.feature-flag/remove-user-from-group
          {:feature-flag-id feature-flag-id
           :grouplink-id (:grouplink_id user)}))}
     "Remove"]]])

(rum/defc group-users-section
  < rum/reactive cmn/cursors
  [app-atom feature-flag-id group]
  (let [users (or (:users group) [])
        existing-user-ids (set (map :user_id users))]
    [:div {:style {:padding "12px 16px" :background "#f9f9f9" :border "1px solid #ddd"}}
     [:div.u-margin-bottom-8
      {:style {:display "flex" :gap "8px"}}
      (buttons/button-web "Add User"
        {:style :primary
         :icon :plus
         :size :small
         :on-click
         #(grid/dispatch-action app-atom :admin.feature-flag/open-add-user-modal
            {:feature-flag-id feature-flag-id
             :group-id (:group_id group)
             :group-name (:group_name group)
             :existing-user-ids existing-user-ids})})
      (buttons/button-web "Add Staff"
        {:style :secondary
         :icon :plus
         :size :small
         :on-click
         #(grid/dispatch-action app-atom :admin.feature-flag/open-add-staff-modal
            {:feature-flag-id feature-flag-id
             :group-id (:group_id group)
             :group-name (:group_name group)})})
      (buttons/button-web "Add MRP Associates"
        {:style :secondary
         :icon :plus
         :size :small
         :on-click
         #(grid/dispatch-action app-atom :admin.feature-flag/confirm-add-mrp-associates
            {:feature-flag-id feature-flag-id
             :group-id (:group_id group)
             :group-name (:group_name group)})})
      (when (seq users)
        (buttons/button-web "Remove All"
          {:style :secondary
           :size :small
           :on-click
           #(grid/dispatch-action app-atom :admin.feature-flag/confirm-remove-all-users
              {:feature-flag-id feature-flag-id
               :group-id (:group_id group)
               :group-name (:group_name group)
               :user-count (count users)})}))]
     (cond
       (seq users)
       [:table {:style {:width "100%" :font-size "12px" :border-collapse "collapse"}}
        [:thead
         [:tr
          [:th {:style {:padding "6px" :border "1px solid #ddd" :text-align "left"}} "Name"]
          [:th {:style {:padding "6px" :border "1px solid #ddd" :text-align "left"}} "Email"]
          [:th {:style {:padding "6px" :border "1px solid #ddd" :text-align "center" :width "80px"}} "Action"]]]
        (into [:tbody]
          (map #(rum/with-key (user-row app-atom feature-flag-id %) (:grouplink_id %))
            users))]

       :else
       [:div {:style {:padding "8px" :color "#666"}} "No users in this group."])]))

(rum/defc group-row < rum/reactive cmn/cursors [app-atom feature-flag-id group]
  (let [process-atom (cmn/process-cursor app-atom)
        expanded-groups (cmn/curact process-atom :ff-expanded-groups)
        expanded? (contains? (or expanded-groups #{}) (:group_id group))
        user-count (or (:user_count group) 0)
        has-users? (pos? user-count)]
    [:<>
     [:tr
      [:td {:style {:padding "6px" :border "1px solid #ddd"}}
       [:a.u-cursor-pointer
        {:on-click #(toggle-expanded process-atom (:group_id group))}
        (str (if expanded? "▼ " "▶ ") (:group_name group))]]
      [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
       user-count]
      [:td {:style {:padding "6px" :border "1px solid #ddd" :text-align "center"}}
       (cond
         has-users?
         [:span
          {:style {:color "#999" :cursor "not-allowed"}
           :title "Remove all users from this group before unlinking it from the feature flag."}
          "Unlink"]

         :else
         [:a.u-cursor-pointer
          {:on-click
           (fn [_]
             (toaster/open-overlay app-atom :admin.feature-flag/group-action)
             (comms/send :admin.feature-flag/remove-group
               {:link-id (:link_id group)
                :feature-flag-id feature-flag-id}))}
          "Unlink"])]]
     (when expanded?
       [:tr
        [:td {:col-span 3 :style {:padding 0}}
         (group-users-section app-atom feature-flag-id group)]])]))

(rum/defc groups-content < rum/reactive cmn/cursors [app-atom flag]
  (let [process-atom (cmn/process-cursor app-atom)
        groups (cmn/curact process-atom :ff-groups)
        available-groups (cmn/curact process-atom :ff-available-groups)
        feature-flag-id (:feature-flag/id flag)
        linked-ids (set (map :group_id (or groups [])))
        unlinked-groups (remove #(linked-ids (:id %)) (or available-groups []))]
    [:div.u-margin-bottom-20
     (headings/heading "Groups" {:type :h4 :size :small :flush true})
     [:p.u-margin-top-4 {:style {:font-size "12px" :color "#666"}}
      "Link groups to this flag to enable it for all users in that group. Expand a group to manage its users."]
     [:div.u-margin-top-8.u-margin-bottom-8
      {:style {:display "flex" :gap "8px"}}
      (buttons/button-web "Create Group"
        {:style :primary
         :icon :plus
         :size :small
         :on-click
         #(grid/dispatch-action app-atom :admin.feature-flag/open-create-group-modal
            {:feature-flag-id feature-flag-id
             :feature-key (:feature-flag/feature-key flag)})})
      (buttons/button-web "Link Existing Group"
        {:style :secondary
         :size :small
         :on-click
         #(grid/dispatch-action app-atom :admin.feature-flag/open-add-group-modal
            {:feature-flag-id feature-flag-id
             :unlinked-groups unlinked-groups})})]
     (cond
       (seq groups)
       [:table {:class (u/bem :oTable [:has-shadow])}
        [:thead
         [:tr
          [:th.u-text-left "Group"]
          [:th.u-text-center {:style {:width "100px"}} "Users"]
          [:th.u-text-center {:style {:width "100px"}} "Action"]]]
        (into [:tbody]
          (map #(rum/with-key (group-row app-atom feature-flag-id %) (:link_id %))
            groups))]

       :else
       [:p.u-margin-top-8.u-text-center {:style {:color "#666"}} "No groups linked to this flag."])
     (toaster/loading-overlay app-atom :admin.feature-flag/group-action)
     (toaster/loading-overlay app-atom :admin.feature-flag/user-action)]))

;; ============================================================================
;; Metadata Section (inline editor, lifted from dashboard modal)
;; ============================================================================

(defn- parse-metadata-pairs [json-str]
  #?(:cljs
     (when-not (str/blank? json-str)
       (try
         (let [to-children
               (fn [m]
                 (mapv (fn [[ck cv]] {:key (name ck) :value (str cv)}) m))]
           (mapv
             (fn [[k v]]
               (cond
                 (map? v)
                 {:key (name k) :children (to-children v)}

                 (string? v)
                 (let [parsed
                       (try (js->clj (.parse js/JSON v) :keywordize-keys true)
                         (catch :default _ nil))]
                   (if (map? parsed)
                     {:key (name k) :children (to-children parsed)}
                     {:key (name k) :value v}))

                 :else
                 {:key (name k) :value (str v)}))
             (js->clj (.parse js/JSON json-str) :keywordize-keys true)))
         (catch :default _ [])))))

(defn- pairs->payload [pairs]
  #?(:cljs
     (mapv
       (fn [{k :key :keys [value children]}]
         (cond
           (seq children)
           {:key k
            :value
            (.stringify js/JSON
              (clj->js (into {} (map (juxt :key :value) children))))
            :nested true}

           :else
           {:key k :value (or value "")}))
       pairs)))

(defmethod comms/receive :admin.feature-flag/update-metadata [app-atom {:keys [success rows]}]
  (toaster/close-overlay app-atom :admin.feature-flag/update-metadata)
  (when success
    (let [process-atom (cmn/process-cursor app-atom)
          updated-metadata (get-in (first rows) [:feature-flag/metadata])]
      (swap! process-atom
        (fn [s]
          (-> s
            (assoc-in [:ff-overview :feature-flag/metadata] updated-metadata)
            (assoc :ff-metadata-pairs (or (parse-metadata-pairs updated-metadata) []))))))))

(rum/defc metadata-content < rum/reactive cmn/cursors [app-atom flag]
  (let [process-atom (cmn/process-cursor app-atom)
        pairs-atom (rum/cursor process-atom :ff-metadata-pairs)
        pairs (or (rum/react pairs-atom) [])]
    [:div.u-relative.u-margin-bottom-20
     [:div
      {:class (u/bem :flex-grid-group [:valign-middle :align-left :flush])}
      [:div
       {:class (u/bem :flex-grid-block [:fluid])}
       [:div.flex-grid-content
        (headings/heading "Metadata" {:type :h4 :size :small :flush true})]]
      [:div
       {:class (u/bem :flex-grid-block [:align-right :fluid])}
       [:div.flex-grid-content
        (buttons/button-web "Save Metadata"
          {:style :primary
           :icon :check
           :size :small
           :on-click
           #(do
              (toaster/open-overlay app-atom :admin.feature-flag/update-metadata)
              (comms/send :admin.feature-flag/update-metadata
                {:feature-flag-id (:feature-flag/id flag)
                 :pairs (pairs->payload pairs)}))})]]]
     [:p.u-margin-top-4 {:style {:font-size "12px" :color "#666"}}
      "Use + to add a key, nest a pair into children, or remove with ×. Click Save Metadata to persist."]
     [:div.u-margin-bottom-8
      (buttons/button
        "+ Add Pair"
        #(swap! pairs-atom (fn [ps] (conj (vec ps) {:key "" :value ""})))
        {:small true})]
     [:table
      {:class (u/bem :oTable [:has-shadow])}
      [:thead
       [:tr
        [:th "Key"]
        [:th "Value"]
        [:th ""]]]
      [:tbody
       (mapcat
         (fn [idx]
           (let [{:keys [children]} (nth pairs idx)
                 parent? (some? children)]
             (cons
               [:tr {:key (str "r" idx)}
                [:td
                 (forms/input-raw "key"
                   (rum/cursor-in pairs-atom [idx :key])
                   {:placeholder "key"})]
                [:td
                 (when-not parent?
                   (forms/input-raw "value"
                     (rum/cursor-in pairs-atom [idx :value])
                     {:placeholder "value"}))]
                [:td.u-text-center
                 [:a.u-cursor-pointer.u-display-block.u-margin-bottom-4
                  {:on-click
                   #(swap! pairs-atom update idx
                      (fn [p]
                        (cond
                          parent?
                          (update p :children conj {:key "" :value ""})

                          :else
                          (-> p (dissoc :value) (assoc :children [{:key "" :value ""}])))))}
                  "+"]
                 [:a.u-cursor-pointer
                  {:on-click
                   #(swap! pairs-atom
                      (fn [ps]
                        (vec (concat (take idx ps) (drop (inc idx) ps)))))}
                  "×"]]]
               (map-indexed
                 (fn [cidx _]
                   [:tr
                    {:key (str "c" idx "-" cidx)
                     :style {:background "#f9f9f9"}}
                    [:td {:style {:padding-left "32px"}}
                     (forms/input-raw "key"
                       (rum/cursor-in pairs-atom [idx :children cidx :key])
                       {:placeholder "key"})]
                    [:td
                     (forms/input-raw "value"
                       (rum/cursor-in pairs-atom [idx :children cidx :value])
                       {:placeholder "value"})]
                    [:td.u-text-center
                     [:a.u-cursor-pointer
                      {:on-click
                       #(swap! pairs-atom update idx
                          (fn [p]
                            (let [new-cs
                                  (vec (concat
                                         (take cidx (:children p))
                                         (drop (inc cidx) (:children p))))]
                              (cond
                                (empty? new-cs)
                                (-> p (dissoc :children) (assoc :value ""))

                                :else
                                (assoc p :children new-cs)))))}
                      "×"]]])
                 children))))
         (range (count pairs)))]]
     (toaster/loading-overlay app-atom :admin.feature-flag/update-metadata)]))

;; ============================================================================
;; Reports Section (datatables)
;; ============================================================================

(def reports-datatable-fns
  {:column-fns {}})

(def reports-datatable-columns
  [{:title "Entity UUID"
    :key :entity_uuid
    :className "dt-center"}
   {:title "Decision"
    :key :decision
    :className "dt-center"}
   {:title "Hash"
    :key :hash_val
    :className "dt-center"}
   {:title "Probability"
    :key :probability
    :className "dt-center"}
   {:title "Version"
    :key :config_version
    :className "dt-center"}
   {:title "Date"
    :key :create_ts
    :type :date
    :dateformat "dd MMM yyyy HH:mm"
    :className "dt-center"}])

(defn reports-datatable-config [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    {:use-wrapper? true
     :server-side? true
     :table-name reports-datatable-key
     :row-id :entity_uuid
     :data-source
     {:action :admin.feature-flag/decisions-list
      :params
      (fn []
        {:feature-key (cmn/curact process-atom [:ff-overview :feature-flag/feature-key])})
      :defer-load? true}
     :options
     {:order :desc
      :sort-title "Date"
      :sticky-header? true
      :paging? true
      :pagination {:page 0 :page-size 10}}
     :column-defs reports-datatable-columns}))

(defmethod grid/action :admin.feature-flag/decisions-list [app-atom _ _]
  (let [table-atom (datatables/table-cursor app-atom reports-datatable-key)
        use-wrapper? (get-in @table-atom [:config :use-wrapper?])]
    (when use-wrapper?
      #?(:cljs
         (when-let [dt-instance (:dt-instance @table-atom)]
           (let [ajax-obj ^js (.-ajax dt-instance)]
             (.reload ajax-obj nil false)))
         :clj nil))))

(defmethod comms/receive :admin.feature-flag/decisions-list [app-atom response]
  (let [table-atom (datatables/table-cursor app-atom reports-datatable-key)
        server-side? (get-in @table-atom [:config :server-side?])
        ajax-callback-atom (:ajax-callback @table-atom)]
    (datatables/overlay app-atom :close reports-datatable-key)
    (when server-side?
      #?(:cljs
         (when ajax-callback-atom
           (ajax/handle-server-response response ajax-callback-atom))
         :clj nil))))

(rum/defc reports-content < rum/reactive cmn/cursors [app-atom]
  [:div.u-relative.u-margin-bottom-20
   (headings/heading "Recent Decisions" {:type :h4 :size :small :flush true})
   (datatables/table app-atom reports-datatable-key reports-datatable-fns)
   (toaster/loading-overlay app-atom reports-datatable-key)])

;; ============================================================================
;; Change History Section (server-side paginated)
;; ============================================================================

(def changelog-datatable-fns
  {:column-fns {}})

(def changelog-datatable-columns
  [{:title "Date"
    :key :dateadded
    :type :date
    :dateformat "dd MMM yyyy HH:mm"
    :className "dt-center"}
   {:title "Changed By"
    :key :changed_by
    :className "dt-center"}
   {:title "Version"
    :key :config_version
    :className "dt-center"}
   {:title "Field"
    :key :field_name
    :className "dt-center"}
   {:title "From"
    :key :old_value}
   {:title "To"
    :key :new_value}])

(defn changelog-datatable-config [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)]
    {:use-wrapper? true
     :server-side? true
     :table-name changelog-datatable-key
     :row-id :dateadded
     :data-source
     {:action :admin.feature-flag/change-log-list
      :params
      (fn []
        {:feature-flag-id (cmn/curact process-atom [:ff-overview :feature-flag/id])})
      :defer-load? true}
     :options
     {:order :desc
      :sort-title "Date"
      :sticky-header? true
      :paging? true
      :pagination {:page 0 :page-size 20}}
     :column-defs changelog-datatable-columns}))

(defmethod grid/action :admin.feature-flag/change-log-list [app-atom _ _]
  (let [table-atom (datatables/table-cursor app-atom changelog-datatable-key)
        use-wrapper? (get-in @table-atom [:config :use-wrapper?])]
    (when use-wrapper?
      #?(:cljs
         (when-let [dt-instance (:dt-instance @table-atom)]
           (let [ajax-obj ^js (.-ajax dt-instance)]
             (.reload ajax-obj nil false)))
         :clj nil))))

(defmethod comms/receive :admin.feature-flag/change-log-list [app-atom response]
  (let [table-atom (datatables/table-cursor app-atom changelog-datatable-key)
        server-side? (get-in @table-atom [:config :server-side?])
        ajax-callback-atom (:ajax-callback @table-atom)]
    (datatables/overlay app-atom :close changelog-datatable-key)
    (when server-side?
      #?(:cljs
         (when ajax-callback-atom
           (ajax/handle-server-response response ajax-callback-atom))
         :clj nil))))

(rum/defc change-history-content < rum/reactive cmn/cursors [app-atom]
  [:div.u-relative.u-margin-bottom-20
   (headings/heading "Change History" {:type :h4 :size :small :flush true})
   (datatables/table app-atom changelog-datatable-key changelog-datatable-fns)
   (toaster/loading-overlay app-atom changelog-datatable-key)])

;; ============================================================================
;; Main Overview Component
;; ============================================================================

(rum/defc feature-flag-overview-screen < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        flag (cmn/curact process-atom :ff-overview)]
    [:div.u-relative
     (cond
       flag
       [:div
        (overview-header app-atom flag)
        (stats-content flag)
        [:hr]
        (description-content app-atom)
        [:hr]
        (groups-content app-atom flag)
        [:hr]
        (metadata-content app-atom flag)
        [:hr]
        (reports-content app-atom)
        [:hr]
        (change-history-content app-atom)]

       :else
       (feedback/feedback :info "Loading"
         [:div "Loading feature flag details..."]))
     (toaster/loading-overlay app-atom :admin.feature-flag/overview)]))

;; ============================================================================
;; Screen Registration and Transitions
;; ============================================================================

(defmethod flow/render-screen :admin.featureflag/overview [app-atom _]
  (feature-flag-overview-screen app-atom))

(defmethod t/transition-after-open :admin.featureflag/overview [app-atom _ _]
  (dcommon/kill-search-bar)
  (let [process-atom (cmn/process-cursor app-atom)
        screen-params (cmn/screen-params app-atom)
        flag-uuid (:uuid screen-params)]
    (swap! process-atom (fn [s] (apply dissoc s overview-keys)))
    (datatables/initiate app-atom (reports-datatable-config app-atom) reports-datatable-fns)
    (datatables/initiate app-atom (changelog-datatable-config app-atom) changelog-datatable-fns)
    (datatables/overlay app-atom :open reports-datatable-key)
    (datatables/overlay app-atom :open changelog-datatable-key)
    (toaster/open-overlay app-atom :admin.feature-flag/overview)
    (comms/send :admin.feature-flag/overview {:uuid flag-uuid})
    (presence/open app-atom {:context/ident :admin.featureflag/overview})))
